/**
 * FarmerDashboard — /weather/farmer
 * A dedicated view for farmers & serious gardeners. Presents today's
 * planting, harvest, irrigation, fertilizer, livestock, gardening,
 * pest, allergy and moon-gardening calls as clear, color-coded cards.
 *
 * Uses the existing `/api/weather-almanac/advisories` endpoint (no new
 * backend work). Adds a compelling farmer-focused header + drought
 * indicator + fishing forecast so the page delivers real-world value.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { useLocality } from "@/contexts/LocalityContext";
import CardIcon from "@/components/CardIcon";
import {
  ArrowLeft, Sprout, Tractor, Droplets, Wind, Sun, Bug, Flower2,
  Fish, Moon, ThermometerSun, CloudRain, Leaf, ShieldAlert, Loader2, TrendingDown,
} from "lucide-react";

const TONE = {
  good:    { color: "#16a34a", bg: "#dcfce7" },
  info:    { color: "#0284c7", bg: "#e0f2fe" },
  caution: { color: "#d97706", bg: "#fef3c7" },
  warning: { color: "#dc2626", bg: "#fee2e2" },
};

const CATEGORY_ICON = {
  planting:   Sprout,
  harvest:    Tractor,
  irrigation: Droplets,
  fertilizer: Flower2,
  livestock:  ShieldAlert,
  watering:   Droplets,
  mulch:      Leaf,
  protect:    ShieldAlert,
};

// Emoji + accent per category — used by CardIcon to give each card
// a colourful graphic anchor. Falls back to a neutral seedling.
const CATEGORY_EMOJI = {
  planting:   { emoji: "🌱", color: "#16a34a" },
  harvest:    { emoji: "🌾", color: "#b45309" },
  irrigation: { emoji: "💧", color: "#0284c7" },
  fertilizer: { emoji: "🧪", color: "#7c3aed" },
  livestock:  { emoji: "🐄", color: "#a16207" },
  watering:   { emoji: "🚿", color: "#0891b2" },
  mulch:      { emoji: "🍂", color: "#a16207" },
  protect:    { emoji: "🛡️", color: "#dc2626" },
};

export default function FarmerDashboard() {
  const { locality } = useLocality();
  const [coords, setCoords] = useState(null);
  const [data,   setData]   = useState(null);
  const [busy,   setBusy]   = useState(true);
  const [error,  setError]  = useState(null);

  useEffect(() => {
    if (locality?.lat && locality?.lng) {
      setCoords({ lat: locality.lat, lng: locality.lng });
      return;
    }
    if (!("geolocation" in navigator)) {
      setCoords({ lat: 40.7128, lng: -74.006 });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => setCoords({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => setCoords({ lat: 40.7128, lng: -74.006 }),
      { timeout: 8000 },
    );
  }, [locality]);

  useEffect(() => {
    if (!coords) return;
    let cancelled = false;
    (async () => {
      setBusy(true); setError(null);
      try {
        const { data } = await http.get(`/weather-almanac/advisories?lat=${coords.lat}&lng=${coords.lng}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Couldn't load advisories");
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [coords]);

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="weather-farmer-page">
      <Link to="/weather" className="inline-flex items-center gap-1 text-sm opacity-70 hover:opacity-100 mb-3">
        <ArrowLeft className="w-3 h-3" /> Weather Almanac
      </Link>

      {/* Vibrant hero */}
      <section className="relative overflow-hidden rounded-3xl border p-6 sm:p-10 mb-6"
               style={{
                 borderColor: "var(--wofy-border)",
                 background: "linear-gradient(135deg, #DCFCE7 0%, #FEF3C7 50%, #FDE68A 100%)",
               }}
               data-testid="farmer-hero">
        <Sun     className="absolute -top-6 -right-6 w-40 h-40 opacity-25 text-amber-500 rotate-12 pointer-events-none" />
        <Sprout  className="absolute -bottom-6 -left-6 w-40 h-40 opacity-30 text-emerald-600 pointer-events-none" />
        <Tractor className="absolute top-8 right-1/4 w-14 h-14 opacity-25 text-amber-700 pointer-events-none hidden md:block" />

        <div className="relative z-10">
          <div className="overline mb-1" style={{ color: "#065f46" }}>FARMER'S DASHBOARD</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold" style={{ color: "#1e3a2f" }}>
            Today's calls for your farm & garden
          </h1>
          <p className="mt-3 max-w-2xl text-base sm:text-lg" style={{ color: "#365d4b" }}>
            Every recommendation below is computed from today's + tomorrow's
            weather for your exact location. Frost, wind, rain, temperature and
            moisture translated into <em>plant this, spray that, hold off,
            harvest now</em>.
          </p>
        </div>
      </section>

      {busy && (
        <div className="wofy-card !p-6 inline-flex items-center gap-2" data-testid="farmer-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Reading today's weather…
        </div>
      )}
      {error && !busy && (
        <div className="wofy-card !p-6" style={{ background: "#FEF3C7" }}>{error}</div>
      )}

      {!busy && data && (
        <>
          {/* Drought indicator strip */}
          {data.drought && (
            <div className="rounded-2xl border p-4 mb-6" data-testid="farmer-drought"
                 style={{ borderColor: "var(--wofy-border)" }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                     style={{
                       background: data.drought.status.includes("drought") ? "#fee2e2"
                                 : data.drought.status === "dry" ? "#fef3c7"
                                 : data.drought.status === "wet" ? "#dbeafe" : "#dcfce7",
                       color: data.drought.status.includes("drought") ? "#dc2626"
                                 : data.drought.status === "dry" ? "#d97706"
                                 : data.drought.status === "wet" ? "#0284c7" : "#16a34a",
                     }}>
                  <TrendingDown className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="text-xs uppercase font-mono opacity-70">30-day rainfall</div>
                  <div className="font-medium">{data.drought.headline}</div>
                  <div className="text-sm opacity-80 mt-0.5">{data.drought.detail}</div>
                </div>
                <div className="text-right hidden sm:block">
                  <div className="font-display text-2xl">{data.drought.total_mm_30d}mm</div>
                  <div className="text-[11px] opacity-60">
                    {Math.round((data.drought.ratio || 0) * 100)}% of normal
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Farmer advisories — 6 cards (planting / harvest / irrigation / fertilizer / livestock) */}
          <SectionHeader icon={Sprout} title="Farm operations" subtitle="Planting, harvest, irrigation, spraying, livestock" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8" data-testid="farmer-ops-grid">
            {(data.farmer || []).map((a, i) => <AdvisoryCard key={i} a={a} />)}
          </div>

          {/* Gardening + moon-gardening */}
          <SectionHeader icon={Flower2} title="Gardening" subtitle="Water, mulch, moon-cycle tips" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8" data-testid="farmer-gardening-grid">
            {(data.gardening || []).map((a, i) => <AdvisoryCard key={`g${i}`} a={a} />)}
            {(data.moon_gardening || []).map((m, i) => (
              <div key={`m${i}`} className="rounded-2xl border p-4"
                   style={{ borderColor: "var(--wofy-border)" }}
                   data-testid={`moon-gard-${i}`}>
                <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider opacity-70">
                  <Moon className="w-3 h-3" /> {m.category}
                </div>
                <div className="mt-1 text-sm">{m.detail}</div>
              </div>
            ))}
          </div>

          {/* Pest forecast */}
          <SectionHeader icon={Bug} title="Pest forecast" subtitle="What insects & diseases are likely today" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="farmer-pests">
            {(data.pests || []).map((p, i) => (
              <div key={i} className="rounded-2xl border p-4 flex gap-3"
                   style={{ borderColor: "var(--wofy-border)" }}
                   data-testid={`pest-${p.pest.toLowerCase().replace(/\s+/g,"-")}`}>
                <CardIcon
                  emoji="🐛"
                  color={p.level === "high" ? "#dc2626" : p.level === "moderate" ? "#d97706" : "#16a34a"}
                  size="md"
                />
                <div className="flex-1 min-w-0">
                  <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded"
                        style={{
                          background: p.level === "high" ? "#fee2e2" : p.level === "moderate" ? "#fef3c7" : "#dcfce7",
                          color:      p.level === "high" ? "#dc2626" : p.level === "moderate" ? "#d97706" : "#16a34a",
                        }}>
                    {p.level}
                  </span>
                  <div className="font-medium text-sm mt-1">{p.pest}</div>
                  <div className="mt-1 text-xs opacity-80">{p.detail}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Allergy */}
          {(data.allergy || []).length > 0 && (
            <>
              <SectionHeader icon={Wind} title="Allergy forecast" subtitle="Pollen, mold and air-quality outlook" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8" data-testid="farmer-allergy">
                {data.allergy.map((a, i) => (
                  <div key={i} className="rounded-2xl border p-4"
                       style={{ borderColor: "var(--wofy-border)" }}>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded"
                            style={{
                              background: a.level === "high" ? "#fee2e2" : "#dcfce7",
                              color:      a.level === "high" ? "#dc2626" : "#16a34a",
                            }}>
                        {a.level}
                      </span>
                      <div className="font-medium text-sm">{a.category}</div>
                    </div>
                    <div className="mt-2 text-xs opacity-80">{a.detail}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Fishing */}
          {data.fishing && (
            <>
              <SectionHeader icon={Fish} title="Fishing today" subtitle={`Score ${data.fishing.score}/100 — ${data.fishing.level}`} />
              <div className="rounded-2xl border p-4 mb-8" data-testid="farmer-fishing"
                   style={{ borderColor: "var(--wofy-border)" }}>
                <div className="text-sm opacity-90 mb-2">{data.fishing.best_hours}</div>
                <ul className="text-xs opacity-80 space-y-1">
                  {(data.fishing.notes || []).map((n, i) => <li key={i}>• {n}</li>)}
                </ul>
              </div>
            </>
          )}

          {/* Outdoor activities */}
          {(data.activities || []).length > 0 && (
            <>
              <SectionHeader icon={Sun} title="Outdoor activities today" subtitle="Good day for…" />
              <div className="flex flex-wrap gap-2 mb-8" data-testid="farmer-activities">
                {data.activities.map((a, i) => (
                  <span key={i}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs border"
                        style={{
                          borderColor: a.rating === "good" ? "#16a34a" : "#dc2626",
                          color:       a.rating === "good" ? "#16a34a" : "#dc2626",
                          background:  a.rating === "good" ? "#dcfce722" : "#fee2e222",
                        }}
                        title={a.note}
                        data-testid={`activity-${a.activity.toLowerCase().replace(/\s+/g,"-")}`}>
                    {a.rating === "good" ? "✓" : "✗"} {a.activity}
                  </span>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function AdvisoryCard({ a }) {
  const t = TONE[a.tone] || TONE.info;
  const em = CATEGORY_EMOJI[a.category] || { emoji: "🌱", color: t.color };
  return (
    <div className="rounded-2xl border p-4 flex gap-3"
         style={{ borderColor: "var(--wofy-border)" }}
         data-testid={`advisory-${a.category}-${a.tone}`}>
      <CardIcon emoji={em.emoji} color={em.color} size="md" />
      <div className="flex-1 min-w-0">
        <span className="text-[10px] font-mono uppercase tracking-wider opacity-70">
          {a.category}
        </span>
        <div className="mt-0.5 font-medium leading-tight" style={{ color: t.color }}>{a.headline}</div>
        <div className="mt-1 text-sm opacity-85">{a.detail}</div>
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="mt-2 mb-3">
      <div className="flex items-center gap-2 font-display text-2xl">
        <Icon className="w-5 h-5" style={{ color: "var(--wofy-primary)" }} /> {title}
      </div>
      {subtitle && <div className="text-xs opacity-70 mt-0.5">{subtitle}</div>}
    </div>
  );
}
